
import { useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useGroups } from "@/hooks/useGroups";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ContractGroup } from "@/lib/types";

// Define the form schema
const formSchema = z.object({
  name: z.string().min(2, "Name must be at least 2 characters"),
  description: z.string().optional(),
});

export type GroupFormValues = z.infer<typeof formSchema>;

interface GroupModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: (group: ContractGroup) => void;
  group?: ContractGroup;
  isLoading?: boolean;
}

export function GroupModal({ 
  open, 
  onOpenChange, 
  onSuccess,
  group,
  isLoading
}: GroupModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { useCreateGroupMutation, useUpdateGroupMutation } = useGroups();
  const createGroup = useCreateGroupMutation();
  const updateGroup = useUpdateGroupMutation();

  // Initialize form
  const form = useForm<GroupFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: group?.name || "",
      description: group?.description || "",
    },
  });

  const onSubmit = async (data: GroupFormValues) => {
    try {
      setIsSubmitting(true);
      
      let result;
      if (group) {
        result = await updateGroup.mutateAsync({
          id: group.id,
          name: data.name,
          description: data.description,
          user_id: group.user_id,
          created_at: group.created_at,
          updated_at: new Date().toISOString(),
        });
      } else {
        result = await createGroup.mutateAsync({
          name: data.name,
          description: data.description,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        });
      }
      
      setIsSubmitting(false);
      form.reset();
      onOpenChange(false);
      
      // Call the success callback with the new group
      if (onSuccess && result) {
        onSuccess(result);
      }
    } catch (error) {
      setIsSubmitting(false);
      console.error("Error submitting form:", error);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{group ? "Edit Group" : "Add New Group"}</DialogTitle>
          <DialogDescription>
            {group ? "Update group details" : "Create a new group that will be available for all contracts."}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Group name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Group description (optional)"
                      {...field}
                      value={field.value || ""}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting || isLoading}>
                {isSubmitting || isLoading ? "Saving..." : group ? "Update Group" : "Create Group"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
